import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,

  type ReactNode,
} from "react";
import { productDetails } from "@/lib/catalog";
import { findVariantId, getRegisteredProduct } from "@/lib/shopify-catalog";

/**
 * État panier du storefront.
 * L'affichage reste local (rapide, sans latence réseau) ; au moment du checkout
 * un panier Shopify est créé via la Storefront API et la commande est traitée par Shopify.
 */

export type CartLine = {
  id: string;
  name: string;
  price: number;
  qty: number;
  variant?: string;
  /** Références pour permettre l'édition de la ligne dans le panier. */
  productId?: string;
  colorId?: string;
  size?: string;
  /** Identifiant de variante Shopify utilisé au checkout. */
  merchandiseId?: string;
};

type ShopState = {
  cart: CartLine[];
  cartCount: number;
  cartTotal: number;
  addToCart: (line: Omit<CartLine, "qty">, openCart?: boolean) => void;
  removeFromCart: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  setLineSize: (id: string, size: string) => void;
  sizesForLine: (line: CartLine) => string[];
  cartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  wishlist: string[];
  toggleWishlist: (id: string) => void;
  isWished: (id: string) => boolean;
  /** Ouvre le formulaire de commande (paiement à la livraison). */
  checkout: (directLines?: DirectLine[]) => Promise<void>;
  checkingOut: boolean;
  checkoutError: string | null;
  /** Lignes prêtes pour la création de commande Shopify. */
  codOpen: boolean;
  setCodOpen: (open: boolean) => void;
  codLines: CodLine[];
  clearCart: () => void;
};

export type DirectLine = {
  merchandiseId: string;
  quantity: number;
  name?: string | undefined;
  price?: number | undefined;
  variant?: string | undefined;
};

export type CodLine = {
  merchandiseId: string;
  quantity: number;
  name: string;
  price: number;
  variant?: string | undefined;
};

const ShopContext = createContext<ShopState | null>(null);
const STORAGE_KEY = "sunset-cart";

/** Fusionne les lignes ayant le même identifiant après édition. */
function mergeLines(lines: CartLine[]) {
  const out: CartLine[] = [];
  for (const l of lines) {
    const found = out.find((o) => o.id === l.id);
    if (found) found.qty += l.qty;
    else out.push({ ...l });
  }
  return out;
}

function sizesForLine(line: CartLine): string[] {
  const shopify = line.productId ? getRegisteredProduct(line.productId) : undefined;
  const product = shopify ?? (line.productId ? productDetails[line.productId] : undefined);
  if (!product) return [];
  const color = product.colors?.find((c) => c.id === line.colorId) ?? product.colors?.[0];
  return (color?.sizes ?? product.sizes ?? []).map((s) => s.size);
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [codOpen, setCodOpen] = useState(false);
  const [codLines, setCodLines] = useState<CodLine[]>([]);
  const cartRef = useRef<CartLine[]>([]);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Persistance panier (le contenu survit au rechargement / retour du checkout).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setCart(JSON.parse(raw) as CartLine[]);
    } catch {
      /* stockage indisponible : panier en mémoire */
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* ignore */
    }
    cartRef.current = cart;
  }, [cart]);

  const addToCart = useCallback((line: Omit<CartLine, "qty">, openCart = true) => {
    setCart((prev) => {
      const found = prev.find((l) => l.id === line.id);
      const next = found
        ? prev.map((l) => (l.id === line.id ? { ...l, qty: l.qty + 1 } : l))
        : [...prev, { ...line, qty: 1 }];
      cartRef.current = next;
      return next;
    });
    setCheckoutError(null);
    if (openCart) setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((id: string) => {
    setCart((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const setQty = useCallback((id: string, qty: number) => {
    setCart((prev) =>
      qty <= 0
        ? prev.filter((l) => l.id !== id)
        : prev.map((l) => (l.id === id ? { ...l, qty: Math.min(10, qty) } : l)),
    );
  }, []);

  const setLineSize = useCallback((id: string, size: string) => {
    setCart((prev) =>
      mergeLines(
        prev.map((l) => {
          if (l.id !== id) return l;
          const shopify = l.productId ? getRegisteredProduct(l.productId) : undefined;
          const product = shopify ?? (l.productId ? productDetails[l.productId] : undefined);
          const color = product?.colors?.find((c) => c.id === l.colorId) ?? product?.colors?.[0];
          const variant = [color?.label ?? product?.colorLabel, `Taille ${size}`]
            .filter(Boolean)
            .join(" · ");
          const merchandiseId = shopify
            ? findVariantId(shopify, { colorLabel: color?.label, size })
            : l.merchandiseId;
          return {
            ...l,
            size,
            variant,
            ...(merchandiseId ? { merchandiseId } : {}),
            id: [l.productId, l.colorId, size].filter(Boolean).join("-"),
          };
        }),
      ),
    );
  }, []);

  const toggleWishlist = useCallback((id: string) => {
    setWishlist((prev) => (prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  /**
   * Prépare la commande : on résout les variantes Shopify puis on ouvre le
   * formulaire de commande. La commande est ensuite créée dans Shopify Admin
   * via la server function `createCodOrder` (paiement à la livraison).
   */
  const checkout = useCallback(async (directLines?: DirectLine[]) => {
    if (checkingOut) return;
    // On lit le panier le plus récent (un ajout juste avant le clic peut ne pas
    // encore être visible dans la closure).
    const current: CartLine[] = cartRef.current;
    if (current.length === 0 && !directLines?.length) return;
    setCheckingOut(true);
    setCheckoutError(null);
    try {
      const resolved: CodLine[] = current
        .map((l): CodLine | null => {
          const shopify = l.productId ? getRegisteredProduct(l.productId) : undefined;
          const merchandiseId =
            l.merchandiseId ??
            (shopify
              ? findVariantId(shopify, {
                  colorLabel: shopify.colors?.find((c) => c.id === l.colorId)?.label,
                  size: l.size,
                })
              : undefined);
          return merchandiseId
            ? { merchandiseId, quantity: l.qty, name: l.name, price: l.price, variant: l.variant }
            : null;
        })
        .filter((l): l is CodLine => l !== null);


      const lines: CodLine[] = directLines?.length
        ? directLines.map((d) => {
            const match = resolved.find((r) => r.merchandiseId === d.merchandiseId);
            return {
              merchandiseId: d.merchandiseId,
              quantity: d.quantity,
              name: d.name ?? match?.name ?? "Article",
              price: d.price ?? match?.price ?? 0,
              variant: d.variant ?? match?.variant,
            };
          })
        : resolved;

      if (lines.length === 0) {
        setCheckoutError("Ces articles ne sont pas encore disponibles à la commande en ligne.");
        return;
      }

      setCodLines(lines);
      setCartOpen(false);
      setCodOpen(true);
    } finally {
      setCheckingOut(false);
    }
  }, [checkingOut]);

  const value = useMemo<ShopState>(
    () => ({
      cart,
      cartCount: cart.reduce((n, l) => n + l.qty, 0),
      cartTotal: cart.reduce((n, l) => n + l.qty * l.price, 0),
      addToCart,
      removeFromCart,
      setQty,
      setLineSize,
      sizesForLine,
      cartOpen,
      setCartOpen,
      wishlist,
      toggleWishlist,
      isWished: (id: string) => wishlist.includes(id),
      checkout,
      checkingOut,
      checkoutError,
      codOpen,
      setCodOpen,
      codLines,
      clearCart,
    }),
    [
      cart,
      cartOpen,
      wishlist,
      addToCart,
      removeFromCart,
      setQty,
      setLineSize,
      toggleWishlist,
      checkout,
      checkingOut,
      checkoutError,
      codOpen,
      codLines,
      clearCart,
    ],
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}

export function formatDH(price: number) {
  return `${new Intl.NumberFormat("fr-MA").format(price)} DH`;
}
